import DashboardLayout from '@/components/DashboardLayout';
import { router, Link } from '@inertiajs/react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, CalendarDays, MapPin, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState } from 'react';

interface EventData {
  id: number;
  title: string;
  event_type: string;
  location: string | null;
  start_date: string;
  end_date: string | null;
  capacity: number | null;
  registrations_count: number;
  available_spots: number | null;
  price_cents: number;
  formatted_price: string;
  is_free: boolean;
  status: string;
  registration_open: boolean;
  is_full: boolean;
  banner_url: string | null;
  created_at: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

interface Props {
  events: EventData[];
  pagination: Pagination;
  filters: { status: string; event_type: string };
}

const TYPE_MAP: Record<string, { label: string; color: string }> = {
  trip: { label: 'Trip', color: 'bg-blue-500/10 text-blue-700 border-blue-200' },
  tournament: { label: 'Torneio', color: 'bg-amber-500/10 text-amber-700 border-amber-200' },
  workshop: { label: 'Workshop', color: 'bg-purple-500/10 text-purple-700 border-purple-200' },
  party: { label: 'Festa', color: 'bg-pink-500/10 text-pink-700 border-pink-200' },
  other: { label: 'Outro', color: 'bg-gray-500/10 text-gray-700 border-gray-200' },
};

const STATUS_MAP: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  draft: { label: 'Rascunho', variant: 'secondary' },
  published: { label: 'Publicado', variant: 'default' },
  closed: { label: 'Encerrado', variant: 'outline' },
  cancelled: { label: 'Cancelado', variant: 'destructive' },
};

export default function Index({ events, pagination, filters }: Props) {
  const [statusFilter, setStatusFilter] = useState(filters.status);
  const [typeFilter, setTypeFilter] = useState(filters.event_type);

  const applyFilters = (status?: string, type?: string) => {
    router.get('/events', {
      status: status ?? statusFilter,
      event_type: type ?? typeFilter,
    }, { preserveState: true });
  };

  const handlePageChange = (page: number) => {
    router.get('/events', { ...filters, page }, { preserveState: true });
  };

  return (
    <DashboardLayout>
      <div className="p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <CalendarDays className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Eventos</h1>
              <p className="text-muted-foreground">Trips, torneios, workshops e mais</p>
            </div>
          </div>
          <Button asChild>
            <Link href="/events/new">
              <Plus className="w-4 h-4 mr-1" /> Novo Evento
            </Link>
          </Button>
        </div>

        {/* Filtros */}
        <div className="flex gap-3 mb-6">
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); applyFilters(v, undefined); }}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="draft">Rascunho</SelectItem>
              <SelectItem value="published">Publicado</SelectItem>
              <SelectItem value="closed">Encerrado</SelectItem>
              <SelectItem value="cancelled">Cancelado</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); applyFilters(undefined, v); }}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              <SelectItem value="trip">Trip</SelectItem>
              <SelectItem value="tournament">Torneio</SelectItem>
              <SelectItem value="workshop">Workshop</SelectItem>
              <SelectItem value="party">Festa</SelectItem>
              <SelectItem value="other">Outro</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Grid de Eventos */}
        {events.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <CalendarDays className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg">Nenhum evento encontrado</p>
            <Button asChild className="mt-4" variant="outline">
              <Link href="/events/new"><Plus className="w-4 h-4 mr-1" /> Criar Evento</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {events.map((event) => {
                const typeInfo = TYPE_MAP[event.event_type] || TYPE_MAP.other;
                const statusInfo = STATUS_MAP[event.status] || STATUS_MAP.draft;
                return (
                  <Card key={event.id} className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => router.visit(`/events/${event.id}`)}>
                    {/* Banner compacto */}
                    <div className="h-28 bg-muted relative">
                      {event.banner_url ? (
                        <img src={event.banner_url} alt={event.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
                          <CalendarDays className="w-8 h-8 text-primary/30" />
                        </div>
                      )}
                      <div className="absolute top-2 left-2">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${typeInfo.color}`}>
                          {typeInfo.label}
                        </span>
                      </div>
                      <div className="absolute top-2 right-2">
                        <Badge variant={statusInfo.variant} className="text-[10px] px-1.5 py-0">{statusInfo.label}</Badge>
                      </div>
                    </div>
                    {/* Info compacto */}
                    <CardContent className="p-3 space-y-1.5">
                      <h3 className="font-semibold text-sm line-clamp-1">{event.title}</h3>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <CalendarDays className="w-3 h-3" />
                          {format(new Date(event.start_date), 'dd MMM', { locale: ptBR })}
                        </span>
                        {event.location && (
                          <span className="flex items-center gap-1 truncate">
                            <MapPin className="w-3 h-3 shrink-0" />
                            <span className="truncate">{event.location}</span>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between pt-1">
                        <span className="flex items-center gap-1 text-xs">
                          <Users className="w-3 h-3 text-muted-foreground" />
                          <span className="font-medium">{event.registrations_count}</span>
                          {event.capacity && <span className="text-muted-foreground">/ {event.capacity}</span>}
                        </span>
                        <span className={`text-xs font-semibold ${event.is_free ? 'text-green-600' : ''}`}>
                          {event.formatted_price}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Paginacao */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-8">
                <span className="text-sm text-muted-foreground">{pagination.total_count} eventos</span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => handlePageChange(pagination.current_page - 1)} disabled={pagination.current_page === 1}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">Pagina {pagination.current_page} de {pagination.total_pages}</span>
                  <Button variant="outline" size="icon" onClick={() => handlePageChange(pagination.current_page + 1)} disabled={pagination.current_page === pagination.total_pages}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
